import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import {
  csvIntoJson,
  jsonToCSV,
} from "../utils/easyMaps/csvAndJsonConverter";

export default function useMapMenu(menuConfig) {
  const {
    mapId,
    title,
    produtos = [],
    setSnackbar,
    onImportComplete,
    exportImport = true,
    isPing = false,
    updateDataElementSocket,
  } = menuConfig || {};

  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openPasswordModal, setOpenPasswordModal] = useState(false);
  const [hostsData, setHostsData] = useState([]);
  const [exportAnchor, setExportAnchor] = useState(null);
  const [importAnchor, setImportAnchor] = useState(null);
  const [previewCsvModal, setPreviewCsvModal] = useState(false);
  const [elementData, setElementData] = useState([]);
  const [duplicatedRows, setDuplicatedRows] = useState([]);
  const [jsonPreviewData, setJsonPreviewData] = useState(null);
  const [previewJsonModal, setPreviewJsonModal] = useState(false);
  const [userPermission, setUserPermission] = useState("");

  const fileInputRef = useRef(null);
  const fileJsonInputRef = useRef(null);

  const menuOpen = Boolean(anchorEl);

  useEffect(() => {
    const role =
      JSON.parse(localStorage.getItem("1"))?.usuarioCliente?.codigoPermissao ||
      "";
    setUserPermission(role);
  }, []);

  useEffect(() => {
    if (produtos && produtos.length > 0) {
      getHosts(produtos[0]?.AcessoLink[0]?.link);
    }
  }, [produtos]);

  async function getHosts(link) {
    try {
      const response = await api.post("/easyMaps/zabbix/hosts", { link });
      setHostsData(response.data);
    } catch (error) {
      console.error(error);
    }
  }

  function findAndFormatHostByName(name) {
    const host = hostsData.find((h) => h.host === name);
    if (!host) return null;
    return {
      host: host.host,
      hostid: host.hostid,
      hostname: host.host,
      interfaces: host.interfaces || [],
      itens: [],
      tags: host.tags || [],
    };
  }

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  async function handleApiDeleteMap() {
    setOpenDeleteModal(false);
    try {
      const response = await api.post("/easyMaps/map/delete", {
        mapId,
        logsLoginClienteId: JSON.parse(localStorage.getItem("logs"))?.id || "",
      });

      if (response.data.status === "Error") {
        setSnackbar({
          children: "Error: Não foi possível deletar o mapa",
          severity: "error",
        });
      } else {
        setSnackbar({
          children: "Mapa deletado com sucesso!",
          severity: "success",
        });
        const url = window.location.href;
        if (url.includes("top")) navigate("/easyMaps/mapas/topologia");
        else if (url.includes("geo")) navigate("/easyMaps/mapas/geolocalizado");
        else navigate("/easyMaps");
      }
    } catch (error) {
      console.error(error);
      setSnackbar({
        children: "Error: Não foi possível se conectar com o servidor",
        severity: "error",
      });
    }
  }

  function downloadCSV(csv, filename = "export.csv") {
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

  function downloadJSON(jsonData, filename = "export.json") {
    const jsonString = JSON.stringify(jsonData, null, 2);
    const blob = new Blob([jsonString], { type: "application/json" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

  async function handleExport() {
    try {
      const response = await api.post("/easyMaps/map/export", { mapId });
      const formatedData = response.data.Element.map((element) => ({
        titulo: element.titulo,
        img: element.img,
        hostname: element.elementConfig.config.hosts[0].host,
        lat: element.elementConfig.config.coordenadas.lat,
        long: element.elementConfig.config.coordenadas.long,
      }));
      const csv = jsonToCSV(formatedData);
      downloadCSV(csv, `${title}-EasyMaps.csv`);
    } catch (error) {
      setSnackbar({
        children: "Error: Não foi possível se conectar com o servidor",
        severity: "error",
      });
    }
  }

  async function handleExportJson() {
    try {
      const response = await api.post("/easyMaps/map/export/deep", { mapId });
      downloadJSON(response.data, `${title}-EasyMaps.json`);
    } catch (error) {
      setSnackbar({
        children: "Error: Não foi possível se conectar com o servidor",
        severity: "error",
      });
    }
  }

  async function handleImportJson(e) {
    if (!e.target.files[0]) {
      setSnackbar({
        children: "Error: Não foi possível ler o arquivo",
        severity: "error",
      });
      return;
    }
    const file = e.target.files[0];
    const reader = new FileReader();
    reader.onload = async function (event) {
      try {
        const jsonData = JSON.parse(event.target.result);
        setJsonPreviewData(jsonData);
        setPreviewJsonModal(true);
      } catch (error) {
        setSnackbar({
          children:
            error?.message ||
            error?.response?.data?.error ||
            "Error: Não foi possível importar os elementos",
          severity: "error",
        });
      }
    };
    reader.onerror = function () {
      setSnackbar({
        children: "Error: Não foi possível ler o arquivo JSON",
        severity: "error",
      });
    };
    reader.readAsText(file);
  }

  async function importJsonFromPreview(parsed) {
    if (!parsed) return;
    try {
      await api.post("/easyMaps/map/import/deep", {
        exportData: parsed,
        targetMapId: mapId,
      });
      setSnackbar({
        children: "Elementos e mapas importados com sucesso!",
        severity: "success",
      });
      setAnchorEl(null);
      onImportComplete();
    } catch (error) {
      setJsonPreviewData(null);
      setSnackbar({
        children:
          error?.response?.data?.error ||
          "Error: Não foi possível importar os elementos",
        severity: "error",
      });
    } finally {
      setPreviewJsonModal(false);
    }
  }

  async function importElements(elements) {
    let logs = JSON.parse(localStorage.getItem("logs"));
    const importedHosts = elements.map((point) => {
      const host = findAndFormatHostByName(point.hostname);
      if (!host) return null;

      return {
        titulo: point.titulo,
        tipo: 0,
        img: point.img,
        mapRefId: null,
        mapId,
        configuracao: {
          coordenadas: {
            lat: point.lat,
            long: point.long,
          },
          hosts: [host],
          produtoId: produtos[0]?.id,
        },
      };
    });
    try {
      await api.post("/easyMaps/element/createMany", {
        elementsData: importedHosts,
        usuarioClienteId: logs.usuarioClienteId,
        logsLoginClienteId: logs.id,
      });
      setSnackbar({
        children: "Elementos importados com sucesso!",
        severity: "success",
      });
      setAnchorEl(null);
      onImportComplete();
    } catch (error) {
      setPreviewCsvModal(false);
      setSnackbar({
        children:
          error?.response?.data?.error ||
          "Error: Não foi possível importar os elementos",
        severity: "error",
      });
    }
  }

  async function handleImportCsv(e) {
    if (!e.target.files[0]) {
      setSnackbar({
        children: "Error: Não foi possível ler o arquivo",
        severity: "error",
      });
      return;
    }
    try {
      const result = await csvIntoJson(e.target.files[0]);
      if (!result.length > 0) {
        throw new Error("Error: O arquivo CSV está vazio");
      }
      setPreviewCsvModal(true);
      setElementData(result);
      setDuplicatedRows(getDuplicatedLatLongRows(result));
    } catch (error) {
      console.error(error);
      setSnackbar({
        children: error.message || "Error: Falha ao importar o arquivo CSV",
        severity: "error",
      });
    }
  }

  function getDuplicatedLatLongRows(data) {
    const used = {};
    const duplicates = new Set();
    data.forEach((row, index) => {
      const key = `${row.lat},${row.long}`;
      if (used[key] !== undefined) {
        duplicates.add(index);
        duplicates.add(used[key]);
      } else {
        used[key] = index;
      }
    });
    return duplicates;
  }

  async function handleSynchronizeElements() {
    try {
      await api.post("/easyMaps/map/synchronizeElements", { mapId });
      setSnackbar({
        children: "Sincronização realizada com sucesso!",
        severity: "success",
      });
    } catch (error) {
      setSnackbar({
        children:
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          "Error: Não foi possível sincronizar elementos com o zabbix",
        severity: "error",
      });
    } finally {
      updateDataElementSocket();
    }
  }

  return {
    anchorEl,
    menuOpen,
    handleMenuOpen,
    handleMenuClose,
    exportImport,
    isPing,
    exportAnchor,
    setExportAnchor,
    importAnchor,
    setImportAnchor,
    userPermission,
    fileInputRef,
    fileJsonInputRef,
    handleExport,
    handleExportJson,
    handleImportCsv,
    handleImportJson,
    importElements,
    importJsonFromPreview,
    handleSynchronizeElements,
    handleApiDeleteMap,
    openDeleteModal,
    setOpenDeleteModal,
    openPasswordModal,
    setOpenPasswordModal,
    previewCsvModal,
    setPreviewCsvModal,
    elementData,
    duplicatedRows,
    previewJsonModal,
    setPreviewJsonModal,
    jsonPreviewData,
    setJsonPreviewData,
  };
}
